import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, InputAdornment, Menu, MenuItem, TextField, Typography } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, useReactTable } from "@tanstack/react-table";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FeedPanelContext } from "@/components/DriverView/ui/FeedPanelContext";
import BellIconOff from "@/components/Icons/BellIconOff";
import BellIconOn from "@/components/Icons/BellIconOn.tsx";
import StatusLight from "@/components/StatusLight";
import { ActionContext } from "@/models/ActionsProvider";
import { subscribeToFeed, unsubscribeFromFeed } from "@/models/actions/clientActions";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery";
import { type NonAliasChannelRow, nonAliasChannelsQuery } from "@/models/repositories/Feed";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import DataTable from "../DataTable";

const columns: ColumnDef<NonAliasChannelRow>[] = [
  {
    accessorKey: "title",
    header: "Channel",
    cell: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: row.original.unread ? 600 : 400,
            color: "white",
          }}
        >
          {row.original.title}
        </Typography>
        {row.original.unread && <StatusLight color="#ef4444" size={8} />}
        {row.original.subscribed && (
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }} aria-label="Subscribed to notifications">
            <BellIconOn fontSize="18" />
          </Box>
        )}
      </Box>
    ),
    meta: { flex: 1 },
    minSize: 150,
    enableSorting: false,
    enableGlobalFilter: true,
  },
];

export default React.memo(function ChannelsTable() {
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { accountEvent } = useContext(ActionContext);
  const { openPanel } = useContext(FeedPanelContext);
  const { trackAction } = useContext(TelemetryContext);
  const navigate = useNavigate();
  const { feedId: activeFeedId } = useParams();
  const [searchValue, setSearchValue] = useState("");
  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | undefined>();
  const [contextMenuTarget, setContextMenuTarget] = useState<NonAliasChannelRow | null>(null);
  const [menuActionDisabled, setMenuActionDisabled] = useState(false);

  const channelRows =
    useReactiveSelectQuery(
      nonAliasChannelsQuery,
      [
        {
          workspaceId: workspaceId ?? "",
          workspaceMembershipId: myCurrentWorkspaceMembership?.id ?? "",
          searchValue,
        },
      ],
      `ChannelsTable:nonAliasChannelsQuery:${workspaceId}`,
    ) ?? [];

  const data: NonAliasChannelRow[] = useMemo(
    () =>
      channelRows.map((row) => ({
        id: row.id,
        title: row.title ?? "",
        unread: row.unread ?? false,
        subscribed: Boolean(row.subscribed),
      })),
    [channelRows],
  );

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: false,
  });

  const handleRowClick = useCallback(
    (row: NonAliasChannelRow) => {
      trackAction(actions.feedNavigate(row.id));
      navigate(`/workspaces/${workspaceId}/channels/${row.id}`);
      openPanel?.();
    },
    [navigate, openPanel, workspaceId, trackAction],
  );

  const handleRowContextMenu = useCallback((row: NonAliasChannelRow, event: React.MouseEvent) => {
    setContextMenuPosition({ top: event.clientY - 6, left: event.clientX + 2 });
    setContextMenuTarget(row);
  }, []);

  const handleContextMenuClose = useCallback(() => {
    setContextMenuPosition(undefined);
    setContextMenuTarget(null);
  }, []);

  const workspaceMembershipId = myCurrentWorkspaceMembership?.id ?? "";

  const handleMarkAsRead = useCallback(() => {
    if (!accountEvent || !contextMenuTarget) return;
    accountEvent("Marked Feed as Read", { feedId: contextMenuTarget.id });
    handleContextMenuClose();
  }, [accountEvent, contextMenuTarget, handleContextMenuClose]);

  const handleSubscribeToNotifications = useCallback(async () => {
    if (!workspaceId || !contextMenuTarget || !workspaceMembershipId) return;
    const feedId = contextMenuTarget.id;
    setMenuActionDisabled(true);
    handleContextMenuClose();
    try {
      await subscribeToFeed({
        workspaceId,
        feedId,
        workspaceMembershipId,
        preference: "all",
      });
    } finally {
      setMenuActionDisabled(false);
    }
  }, [workspaceId, contextMenuTarget, workspaceMembershipId, handleContextMenuClose]);

  const getRowClassName = useCallback(
    (row: NonAliasChannelRow) => (row.id === activeFeedId ? "channel-row-active" : ""),
    [activeFeedId],
  );

  const handleUnsubscribeFromNotifications = useCallback(async () => {
    if (!workspaceId || !contextMenuTarget || !workspaceMembershipId) return;
    const feedId = contextMenuTarget.id;
    setMenuActionDisabled(true);
    handleContextMenuClose();
    try {
      await unsubscribeFromFeed({
        workspaceId,
        feedId,
        workspaceMembershipId,
      });
    } finally {
      setMenuActionDisabled(false);
    }
  }, [workspaceId, contextMenuTarget, workspaceMembershipId, handleContextMenuClose]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>
      <Typography variant="h6" sx={{ color: "white", fontWeight: 600, mb: 1.5 }}>
        Channels
      </Typography>
      <TextField
        size="small"
        placeholder="Search channels..."
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "rgb(161, 161, 170)", fontSize: 20 }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            bgcolor: "rgb(39, 39, 42)",
            color: "white",
            "& fieldset": { borderColor: "rgb(63, 63, 70)" },
            "&:hover fieldset": { borderColor: "rgb(82, 82, 91)" },
            "&.Mui-focused fieldset": { borderColor: "rgb(113, 113, 122)" },
          },
          "& .MuiInputBase-input::placeholder": {
            color: "rgb(161, 161, 170)",
            opacity: 1,
          },
        }}
      />
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <DataTable
            table={table}
            onRowClick={handleRowClick}
            onRowContextMenu={handleRowContextMenu}
            getRowClassName={getRowClassName}
            pagination={false}
            virtualize
            rowHeight={40}
            headerHeight={32}
            sx={{
              "& .MuiTableContainer-root": { border: "none" },
              "& .MuiTableHead-root .MuiTableCell-root": {
                bgcolor: "rgb(39, 39, 42)",
                color: "rgb(161, 161, 170)",
                fontWeight: 600,
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              },
              "& .MuiTableCell-root": {
                py: 0.5,
                px: 1.5,
                borderBottom: "1px solid rgb(39, 39, 42)",
              },
              "& .MuiTableRow-root:hover:not(.channel-row-active)": {
                bgcolor: "rgb(39, 39, 42)",
              },
              "& .channel-row-active": {
                bgcolor: "rgb(82, 82, 91)",
                "&:hover": {
                  bgcolor: "rgb(82, 82, 91)",
                },
              },
            }}
          />
        </Box>
      </Box>
      {contextMenuTarget != null && contextMenuPosition != null && (
        <Menu
          open
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={contextMenuPosition}
        >
          {contextMenuTarget.unread && (
            <MenuItem
              disabled={menuActionDisabled}
              onClick={handleMarkAsRead}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
                <DraftsOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>Mark as read</Box>
            </MenuItem>
          )}
          {!contextMenuTarget.subscribed && (
            <MenuItem
              disabled={menuActionDisabled}
              onClick={handleSubscribeToNotifications}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
                <BellIconOn fontSize="18" />
              </Box>
              <Box>Subscribe to notifications</Box>
            </MenuItem>
          )}
          {contextMenuTarget.subscribed && (
            <MenuItem
              disabled={menuActionDisabled}
              onClick={handleUnsubscribeFromNotifications}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
                <BellIconOff fontSize="18" />
              </Box>
              <Box>Turn off notifications for this channel</Box>
            </MenuItem>
          )}
        </Menu>
      )}
    </Box>
  );
});
